/**
 * CFS collection page behaviour (build spec §4.5).
 *
 * <cfs-stock-toggle>: "Hide out of stock" switch bound to the Availability filter. It ticks the
 * filter's own "In stock" input in Horizon's facets form (so results update without a reload and
 * the URL keeps the state); if that filter isn't configured it falls back to the URL parameter.
 *
 * Back-button position: when a product in an infinite-scroll grid is opened, the current URL is
 * replaced with ?page=N#{grid item id}, as Horizon's own product card does, so Back returns to the
 * same page of results with that product in view (sections/main-collection.liquid scrolls to it).
 *
 * Back/forward through filter changes: Horizon pushes a history entry per filter change but does
 * not re-render on popstate, so the results here are re-rendered for the restored URL.
 */
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

class CfsStockToggle extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('input'));
    this.param = this.dataset.param || 'filter.v.availability';
    this.input.addEventListener('change', this.#onChange);
    document.addEventListener(StandardEvents.collectionUpdate, this.#sync);
    window.addEventListener('popstate', this.#sync);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.collectionUpdate, this.#sync);
    window.removeEventListener('popstate', this.#sync);
  }

  /** The "In stock" input of the Availability filter in a visible facets form. */
  #filterInput() {
    const inputs = /** @type {HTMLInputElement[]} */ ([
      ...document.querySelectorAll(`facets-form-component input[name="${this.param}"][value="1"]`),
    ]);
    return inputs.find((input) => input.closest('form')?.getClientRects().length) ?? inputs[0] ?? null;
  }

  #onChange = () => {
    const want = this.input.checked;
    const filterInput = this.#filterInput();
    if (filterInput) {
      if (filterInput.checked !== want) {
        filterInput.checked = want;
        filterInput.dispatchEvent(new Event('change', { bubbles: true }));
        filterInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(this.param);
    url.searchParams.delete('page');
    if (want) url.searchParams.append(this.param, '1');
    window.location.assign(url.toString());
  };

  #sync = () => {
    const values = new URL(window.location.href).searchParams.getAll(this.param);
    this.input.checked = values.includes('1') && !values.includes('0');
  };
}

if (!customElements.get('cfs-stock-toggle')) customElements.define('cfs-stock-toggle', CfsStockToggle);

// Back-button position for CFS cards in infinite-scroll grids.
document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target).closest?.('cfs-product-card a[href*="/products/"]');
  if (!link || window.Shopify?.designMode) return;
  const item = /** @type {HTMLElement | null} */ (link.closest('results-list[infinite-scroll="true"] li[id][data-page]'));
  if (!item) return;
  const url = new URL(window.location.href);
  url.hash = item.id;
  url.searchParams.set('page', item.dataset.page ?? '1');
  history.replaceState(history.state, '', url.toString());
});

// Re-render results when Back/Forward changes the query (filters, sort, page), not for hash-only moves.
// The last query is tracked through pushState/replaceState because Horizon updates the URL itself.
let lastQuery = window.location.search;
for (const method of /** @type {const} */ (['pushState', 'replaceState'])) {
  const original = history[method];
  history[method] = function (...args) {
    const result = original.apply(this, /** @type {any} */ (args));
    lastQuery = window.location.search;
    return result;
  };
}
window.addEventListener('popstate', () => {
  if (window.location.search === lastQuery) return;
  lastQuery = window.location.search;
  const list = document.querySelector('results-list[section-id]');
  const sectionId = list?.getAttribute('section-id');
  if (sectionId) sectionRenderer.renderSection(sectionId, { cache: false });
});
